import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { suivreBudgets } from '../../analysis/budgets'
import { nomCategorie } from '../../analysis/donnees'
import { previsionnelDuMois, type LignePrevisionnel, type Valeur } from '../../analysis/previsionnel'
import { mouvements, soldeAu } from '../../analysis/soldes'
import { formatMontant, somme } from '../../domain/argent'
import { ajouterJours, aujourdhui, dernierJourDuMois, moisCourant, premierJourDuMois } from '../../domain/temps'
import type { MoisKey } from '../../domain/types'
import { Bascule, Carte, Encart, Entete, Jauge, Montant, NavMois, Pastille, Puce, SelectCompte } from '../composants'
import { useDonnees } from '../donnees'

type Lecture = 'charge' | 'flux'

export function PagePrevisionnel() {
  const { d, ix, reglages } = useDonnees()
  const [params, setParams] = useSearchParams()
  const mois: MoisKey = params.get('mois') ?? moisCourant()
  const compteId = params.get('compte')
  const lecture: Lecture = params.get('lecture') === 'flux' ? 'flux' : 'charge'
  const jour = aujourdhui()

  const maj = (cle: string, v: string | null) => {
    const p = new URLSearchParams(params)
    if (v) p.set(cle, v)
    else p.delete(cle)
    setParams(p, { replace: true })
  }

  const prev = useMemo(() => previsionnelDuMois(ix, mois, jour, compteId, reglages), [ix, mois, jour, compteId, reglages])
  const budgets = useMemo(() => suivreBudgets(ix, mois, jour, compteId), [ix, mois, jour, compteId])

  // Tresorerie : toujours en flux, puisqu'il s'agit d'argent sur un compte.
  const tresorerie = useMemo(() => {
    const mvts = mouvements(ix)
    const comptes = d.comptes.filter((c) => (compteId ? c.id === compteId : !c.archive))
    const debut = somme(comptes.map((c) => soldeAu(c, mvts, ajouterJours(premierJourDuMois(mois), -1))))
    const remboursements = somme(
      mvts
        .filter((m) => m.source === 'reglement' && m.date.startsWith(mois) && comptes.some((c) => c.id === m.compteId))
        .map((m) => m.montant),
    )
    const fin = debut + prev.revenus.projection.flux - prev.depenses.projection.flux + remboursements
    const actuel = mois === moisCourant() ? somme(comptes.map((c) => soldeAu(c, mvts, jour))) : null
    return { debut, fin, remboursements, actuel }
  }, [ix, d.comptes, compteId, mois, prev, jour])

  const depenses = prev.lignes.filter((l) => l.sens === 'depense')
  const revenus = prev.lignes.filter((l) => l.sens === 'revenu')
  const v = (x: Valeur) => x[lecture]

  return (
    <>
      <Entete titre="Budget" sous="Prévisionnel du mois" action={<Link to="/budgets" className="btn btn--petit">Enveloppes</Link>} />
      <main className="contenu">
        <NavMois mois={mois} onChange={(m) => maj('mois', m)} />
        <div className="filtres">
          <SelectCompte comptes={d.comptes} valeur={compteId} onChange={(c) => maj('compte', c)} tous="Tous les comptes" />
          <Bascule
            etiquette="Lecture"
            valeur={lecture}
            onChange={(l) => maj('lecture', l === 'flux' ? 'flux' : null)}
            options={[
              { valeur: 'charge', libelle: 'À ma charge' },
              { valeur: 'flux', libelle: 'Trésorerie' },
            ]}
          />
        </div>
        <p className="tres-doux">
          {lecture === 'charge'
            ? 'Les dépenses partagées ne comptent que pour votre part : c’est la lecture des budgets.'
            : 'Les dépenses comptent pour leur montant total, tel qu’il sort du compte.'}
        </p>

        <Carte titre={compteId ? `Trésorerie — ${ix.compteParId.get(compteId)?.nom}` : 'Trésorerie, tous comptes'}>
          <div className="mesures">
            <div className="mesure">
              <div className="mesure__valeur"><Montant valeur={tresorerie.debut} /></div>
              <div className="mesure__libelle">début du mois</div>
            </div>
            {tresorerie.actuel !== null && (
              <div className="mesure">
                <div className="mesure__valeur"><Montant valeur={tresorerie.actuel} /></div>
                <div className="mesure__libelle">aujourd’hui</div>
              </div>
            )}
            <div className="mesure">
              <div className="mesure__valeur"><Montant valeur={tresorerie.fin} /></div>
              <div className="mesure__libelle">{prev.temporalite === 'passe' ? 'fin du mois' : 'fin du mois (projeté)'}</div>
            </div>
          </div>
          {tresorerie.remboursements > 0 && (
            <p className="tres-doux" style={{ marginTop: 8 }}>dont {formatMontant(tresorerie.remboursements)} de remboursements reçus</p>
          )}
        </Carte>

        {budgets.some((b) => b.budget.periode === 'annuel') && (
          <Carte titre="Enveloppes annuelles">
            <div className="pile">
              {budgets
                .filter((b) => b.budget.periode === 'annuel')
                .map((s) => (
                  <div key={s.budget.id}>
                    <div className="ligne ligne--espacee doux">
                      <span>{nomCategorie(ix, s.budget.categorieId)}</span>
                      <span className="mono"><Montant valeur={s.consomme} /> / <Montant valeur={s.plafond} /></span>
                    </div>
                    <Jauge taux={s.taux} ecoule={s.ecoule} etat={s.etat} />
                  </div>
                ))}
            </div>
          </Carte>
        )}

        <Carte
          titre="Dépenses"
          action={
            <span className="gras mono">
              <Montant valeur={v(prev.depenses.projection)} />
            </span>
          }
        >
          {depenses.length === 0 ? (
            <p className="tres-doux">Rien de prévu ni d’estimé pour ce mois.</p>
          ) : (
            <div className="pile">
              {depenses.map((l) => (
                <LigneCategorie key={l.categorieId} l={l} lecture={lecture} temporalite={prev.temporalite} />
              ))}
            </div>
          )}
        </Carte>

        <Carte titre="Revenus" action={<span className="gras mono"><Montant valeur={prev.revenus.projection.flux} /></span>}>
          {revenus.length === 0 ? (
            <p className="tres-doux">Aucun revenu prévu. Déclarez votre salaire comme récurrence pour qu’il apparaisse d’avance.</p>
          ) : (
            <div className="pile">
              {revenus.map((l) => (
                <LigneCategorie key={l.categorieId} l={l} lecture="flux" temporalite={prev.temporalite} />
              ))}
            </div>
          )}
        </Carte>

        {prev.temporalite !== 'passe' && (
          <Encart>
            <strong>Prévu</strong> : échéances récurrentes pas encore saisies. <strong>Estimé</strong> : dépenses variables attendues
            d’après l’historique, déduction faite de ce qui est déjà saisi. La méthode se règle dans{' '}
            <Link to="/reglages">Réglages</Link>.
          </Encart>
        )}
        <Link to={`/recurrences?mois=${mois}`} className="btn btn--bloc">Échéancier du mois ({premierJourDuMois(mois).slice(8)}–{dernierJourDuMois(mois).slice(8)})</Link>
      </main>
    </>
  )
}

function LigneCategorie({ l, lecture, temporalite }: { l: LignePrevisionnel; lecture: Lecture; temporalite: 'passe' | 'courant' | 'futur' }) {
  const { ix } = useDonnees()
  const cat = ix.categorieParId.get(l.categorieId)
  const projection = l.projection[lecture]
  const plafond = lecture === 'charge' ? l.budget : null
  const taux = plafond ? projection / plafond : 0
  const realise = l.realise[lecture]

  return (
    <div>
      <div className="ligne ligne--espacee">
        <span className="ligne">
          <Pastille couleur={cat?.couleur ?? '#999'} />
          <Link to={`/operations?categorie=${l.categorieId}`}>{cat?.nom ?? '—'}</Link>
          {l.echeancesEnRetard > 0 && <Puce ton="alerte">{l.echeancesEnRetard} à saisir</Puce>}
        </span>
        <span className="mono gras">
          <Montant valeur={projection} />
          {plafond !== null && <span className="tres-doux"> / {formatMontant(plafond)}</span>}
        </span>
      </div>
      {plafond !== null && <Jauge taux={taux} etat={projection > plafond ? 'depasse' : taux > 0.9 ? 'attention' : 'ok'} />}
      <div className="tres-doux mono">
        réalisé {formatMontant(realise)}
        {l.prevu[lecture] > 0 && <> · prévu {formatMontant(l.prevu[lecture])}</>}
        {temporalite !== 'passe' && l.resteEstime[lecture] > 0 && <> · estimé {formatMontant(l.resteEstime[lecture])}</>}
        {temporalite !== 'passe' && l.source && l.estimationVariable.flux > 0 && <> ({l.source})</>}
      </div>
    </div>
  )
}
